import {
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  query,
  setDoc,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { tratarErro, tratarErroDeCallable } from '@/lib/erros';

/**
 * ★★★ WT lote 1 — TODA CALLABLE PASSA POR AQUI.
 *
 * ⚠⚠ Erro de callable chega ao cliente com o `code` do servidor (`permission-denied`,
 * `failed-precondition`, `resource-exhausted`) — e é **o diagnóstico mais valioso que
 * existe**: ele diz que a tela pediu algo que o servidor recusou. ★ Até agora ele só
 * aparecia na mensagem que a pessoa lia e esquecia.
 *
 * ★ O embrulho **registra e relança**: quem chama continua tratando o erro como antes
 * (as telas leem `e.code` para escolher a mensagem). ⚠ Engolir aqui quebraria todas elas.
 */
const chamarCallable = (nome) => async (dados) => {
  try {
    return await httpsCallable(functions, nome)(dados);
  } catch (erro) {
    throw tratarErroDeCallable(nome, erro);
  }
};


/**
 * Remuneração e fechamento do mês (VC).
 *
 * A CONFIG é escrita pelo gestor daqui. O SNAPSHOT do fechamento **não**: ele só
 * é gravado pela callable `fecharMes`, com o Admin SDK. Ver a explicação nas
 * `firestore.rules` — se a tela pudesse escrevê-lo, o mês fechado deixaria de
 * ser prova do que foi pago.
 */

const refConfig = (clinicaId, uid) => doc(db, 'clinicas', clinicaId, 'remuneracao', uid);
const colConfigs = (clinicaId) => collection(db, 'clinicas', clinicaId, 'remuneracao');
const refFechamento = (clinicaId, mes) => doc(db, 'clinicas', clinicaId, 'fechamentos', mes);
const refPessoaNoMes = (clinicaId, mes, uid) =>
  doc(db, 'clinicas', clinicaId, 'fechamentos', mes, 'pessoas', uid);

/** Configs de todas as pessoas — só o gestor consegue ler a coleção inteira. */
export function ouvirConfigs(clinicaId, aoReceber, aoFalhar) {
  return onSnapshot(
    colConfigs(clinicaId),
    (snap) => aoReceber(Object.fromEntries(snap.docs.map((d) => [d.id, { id: d.id, ...d.data() }]))),
    tratarErro('as configurações de remuneração', aoFalhar),
  );
}

/** A config da própria pessoa — o que o SDR consegue ler. */
export function ouvirMinhaConfig(clinicaId, uid, aoReceber, aoFalhar) {
  return onSnapshot(
    refConfig(clinicaId, uid),
    (snap) => aoReceber(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    tratarErro('a sua configuração de remuneração', aoFalhar),
  );
}

export async function salvarConfig(clinicaId, uid, dados, autor) {
  await setDoc(
    refConfig(clinicaId, uid),
    {
      modelo: dados.modelo,
      valorPorComparecimento: Math.max(0, Number(dados.valorPorComparecimento) || 0),
      percentual: Math.max(0, Number(dados.percentual) || 0),
      atualizadoEm: serverTimestamp(),
      atualizadoPorId: autor?.id ?? '',
      atualizadoPorNome: autor?.nome ?? '',
    },
    // `merge` para não apagar campos que uma versão futura acrescente; o
    // formulário manda os três sempre.
    { merge: true },
  );
}

/**
 * ★★ WJ (29/set/2026) — TIRAR ALGUÉM DA FOLHA.
 *
 * Apaga a config de uma pessoa. É o ÚNICO caminho para sair da folha, e a razão
 * é medida: `consolidarFechamento` monta a lista a partir das CHAVES de
 * `configs` (`remuneracao/calculo.js:229`) — não do papel, não da função. Quem
 * tem documento aqui é calculado; quem não tem, não aparece. Enquanto este
 * botão não existia, o gestor só sairia da folha por escrita direta no banco.
 *
 * ⚠ O QUE ISTO **NÃO** FAZ: não toca em fechamento nenhum. `fechamentos` tem
 * `allow write: if false` para todos (`firestore.rules:495` e `:502`), então o
 * que já foi pago continua registrado — é a promessa da VC, e ela não depende
 * deste caminho ser bem-comportado: o servidor recusaria.
 *
 * ⚠ O QUE SE PERDE: `atualizadoEm`/`atualizadoPorId`/`atualizadoPorNome`, a
 * trilha de quem configurou por último. Os três campos de valor se refazem
 * preenchendo de novo (medido: as três configs da Sorridents são iguais —
 * R$ 2,00 + 0,7 %), mas a trilha não volta. Trilha de REMOÇÃO seria coleção
 * nova, e ficou fora desta peça de propósito.
 */
export async function removerConfig(clinicaId, uid) {
  await deleteDoc(refConfig(clinicaId, uid));
}

/**
 * Recebimentos de um mês, de todos os leads da clínica.
 *
 * ★ A JANELA É DE PROPÓSITO MAIS LARGA QUE O MÊS (um dia para cada lado).
 *
 * A consulta serve só para não trazer o histórico inteiro; **quem decide o mês é
 * o `mesDoRecebimento`**, em memória, comparando texto. Um `>=`/`<=` colado no
 * primeiro e no último instante do mês dependeria do fuso de quem calcula — e
 * quem calcula pode ser o navegador (America/Sao_Paulo) ou uma Cloud Function
 * (UTC). Com a folga de um dia, nenhum documento da borda escapa da consulta,
 * e a decisão fica com a regra que não tem fuso.
 *
 * Por que `collectionGroup` e não ler lead por lead: ler os leads com
 * `valorRecebido > 0` custa uma consulta por lead que ALGUM DIA recebeu alguma
 * coisa — cresce com o histórico inteiro e nunca diminui, para responder sempre
 * sobre um mês. Aqui o custo é o movimento do mês consultado.
 */
export async function buscarRecebimentosDoMes(clinicaId, mes) {
  const [ano, m] = mes.split('-').map(Number);
  const de = new Date(ano, m - 1, 1, 0, 0, 0, 0);
  de.setDate(de.getDate() - 1);
  const ate = new Date(ano, m, 0, 23, 59, 59, 999);
  ate.setDate(ate.getDate() + 1);
  return buscarRecebimentosEntre(clinicaId, de, ate);
}

/**
 * Os recebimentos da clínica com `data` numa janela qualquer (VD).
 *
 * É a MESMA consulta do mês, parametrizada — mesmo `collectionGroup`, mesmos
 * campos, mesmo índice. Existe para o relatório geral buscar de uma vez a
 * janela que cobre dia, semana e mês do recorte escolhido, sem três leituras.
 */
export async function buscarRecebimentosEntre(clinicaId, de, ate) {
  const q = query(
    collectionGroup(db, 'recebimentos'),
    where('clinicaId', '==', clinicaId),
    where('data', '>=', de),
    where('data', '<=', ate),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** O cabeçalho do fechamento — só o gestor lê. `null` = mês ainda aberto. */
/**
 * ★★ Travas do fechamento (03/11/2026, item 3) — os meses com `status: 'fechado'`.
 * Poucos documentos (um por mês de folha); leitura só do gestor, como a tela.
 */
export async function buscarMesesFechados(clinicaId) {
  const snap = await getDocs(collection(db, 'clinicas', clinicaId, 'fechamentos'));
  return new Set(snap.docs.filter((d) => d.data()?.status === 'fechado').map((d) => d.id));
}

/**
 * ★★ Item 3 — o mês tem ALGUM recebimento? Uma leitura (`limit(1)`), na janela exata do mês no
 * calendário do navegador (São Paulo). ⚠ Sem a folga de um dia da folha: aqui um recebimento do
 * dia 31 do mês anterior não pode contar como movimento deste. As escritas gravam meio-dia, então
 * a borda não é ambígua. Mesmo índice do `buscarRecebimentosEntre` (clinicaId + data).
 */
export async function haRecebimentoNoMes(clinicaId, mes) {
  const [ano, m] = mes.split('-').map(Number);
  const q = query(
    collectionGroup(db, 'recebimentos'),
    where('clinicaId', '==', clinicaId),
    where('data', '>=', new Date(ano, m - 1, 1, 0, 0, 0, 0)),
    where('data', '<=', new Date(ano, m, 0, 23, 59, 59, 999)),
    limit(1),
  );
  return !(await getDocs(q)).empty;
}

export function ouvirFechamento(clinicaId, mes, aoReceber, aoFalhar) {
  return onSnapshot(
    refFechamento(clinicaId, mes),
    (snap) => aoReceber(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    tratarErro('o fechamento do mês', aoFalhar),
  );
}

/**
 * O extrato da pessoa num mês.
 *
 * A EXISTÊNCIA deste documento é o sinal de "mês fechado" para ela: o cabeçalho
 * do fechamento traz os totais da folha e é só do gestor, então a pessoa não tem
 * como consultá-lo para saber.
 */
export async function buscarMeuExtrato(clinicaId, mes, uid) {
  const snap = await getDoc(refPessoaNoMes(clinicaId, mes, uid));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/** As linhas de um mês fechado, para o painel do gestor. */
export async function buscarPessoasDoFechamento(clinicaId, mes) {
  const snap = await getDocs(collection(db, 'clinicas', clinicaId, 'fechamentos', mes, 'pessoas'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function fecharMes(clinicaId, mes) {
  const { data } = await chamarCallable('fecharMes')({ clinicaId, mes });
  return data;
}

export async function reabrirMes(clinicaId, mes, motivo) {
  const { data } = await chamarCallable('reabrirMes')({ clinicaId, mes, motivo });
  return data;
}
